import { useEffect, useState, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Scale, Plus, Trash2, Target } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useUnit } from "../context/UnitContext";

const today = () => new Date().toISOString().slice(0, 10);

export const BodyweightSection = () => {
  const { unit, dw, toLb } = useUnit();
  const [entries, setEntries] = useState([]);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [goal, setGoal] = useState(null);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalVal, setGoalVal] = useState("");

  const load = useCallback(() => {
    api.get("/bodyweight").then(({ data }) => setEntries(data));
    api.get("/bodyweight/goal").then(({ data }) => setGoal(data.target)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveGoal = async () => {
    const target = goalVal ? toLb(parseFloat(goalVal)) : null;
    await api.put("/bodyweight/goal", { target });
    setGoal(target);
    setEditingGoal(false);
    toast.success(target ? "Objectif fixé" : "Objectif retiré");
  };

  const add = async () => {
    if (!value) return;
    setSaving(true);
    try {
      await api.post("/bodyweight", { date: today(), poids: toLb(parseFloat(value)) });
      toast.success("Poids enregistré");
      setValue("");
      load();
    } catch (e) {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/bodyweight/${id}`);
    load();
  };

  const last = entries[entries.length - 1];
  const first = entries[0];
  const delta = last && first ? dw(last.poids) - dw(first.poids) : 0;

  return (
    <section data-testid="bodyweight-section">
      <h2 className="font-display text-2xl text-white mb-2 flex items-center gap-2">
        POIDS CORPOREL <Scale size={18} className="text-[#FF3B30]" />
      </h2>
      <div className="flex gap-2">
        <input data-testid="bodyweight-input" type="number" inputMode="decimal" placeholder={`Ton poids (${unit})`}
          className="set-input flex-1 !text-left !px-3" value={value} onChange={(e) => setValue(e.target.value)} />
        <button data-testid="bodyweight-add-button" disabled={!value || saving} onClick={add}
          className="h-11 px-4 bg-[#FF3B30] text-white text-sm font-semibold flex items-center gap-1.5 hover:bg-[#e0322a] transition-colors disabled:opacity-30">
          <Plus size={16} /> Ajouter
        </button>
        <button data-testid="bodyweight-goal-button" onClick={() => { setEditingGoal(!editingGoal); setGoalVal(goal ? String(dw(goal)) : ""); }}
          className={`h-11 w-11 flex items-center justify-center border transition-colors ${
            goal ? "border-[#FFC107] text-[#FFC107]" : "border-white/15 text-[#B0B0B4] hover:text-white"}`}
          title="Objectif de poids">
          <Target size={16} />
        </button>
      </div>
      {editingGoal && (
        <div className="flex gap-1.5 mt-2 items-center">
          <input data-testid="bodyweight-goal-input" type="number" inputMode="decimal" autoFocus
            placeholder={`Poids cible (${unit}) — vide pour retirer`} className="set-input flex-1 !text-left !px-3"
            value={goalVal} onChange={(e) => setGoalVal(e.target.value)} />
          <button data-testid="bodyweight-goal-save" onClick={saveGoal}
            className="h-11 px-4 bg-[#FFC107] text-black text-sm font-semibold">OK</button>
        </div>
      )}
      {entries.length === 0 ? (
        <p className="text-[#B0B0B4] text-xs mt-2">Pèse-toi une fois par semaine, idéalement le matin à jeun.</p>
      ) : (
        <>
          <div className="flex gap-4 mt-3 text-xs text-[#ccc] flex-wrap">
            <span>Actuel : <span className="text-white font-mono font-bold">{dw(last.poids)} {unit}</span></span>
            {entries.length > 1 && (
              <span>Depuis le début : <span className={`font-mono font-bold ${delta > 0 ? "text-[#FF3B30]" : "text-[#32D74B]"}`}>
                {delta > 0 ? "+" : ""}{Math.round(delta * 10) / 10} {unit}</span></span>
            )}
            {goal && (
              <span data-testid="bodyweight-goal-info">Objectif : <span className="text-[#FFC107] font-mono font-bold">{dw(goal)} {unit}</span>
                <span className="text-[#B0B0B4]"> ({Math.round(Math.abs(dw(last.poids) - dw(goal)) * 10) / 10} {unit} restant)</span></span>
            )}
          </div>
          <div className="bg-[#14141A] border border-[#2E2E3A] mt-2 p-3 h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={entries.map((e) => ({ ...e, poids: dw(e.poids) }))} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <XAxis dataKey="date" stroke="#555" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#555" fontSize={10} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#1C1C1E", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }} />
                {goal && <ReferenceLine y={dw(goal)} stroke="#FFC107" strokeDasharray="5 4"
                  label={{ value: `Objectif ${dw(goal)}`, fill: "#FFC107", fontSize: 10, position: "insideTopRight" }} />}
                <Line type="monotone" dataKey="poids" stroke="#32D74B" strokeWidth={2.5} dot={{ fill: "#32D74B", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-[#14141A] border border-[#2E2E3A] mt-2 divide-y divide-white/5 max-h-36 overflow-y-auto">
            {[...entries].reverse().map((e) => (
              <div key={e.id} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                <span className="text-[#B0B0B4] flex-1">{e.date}</span>
                <span className="text-white font-mono">{dw(e.poids)} {unit}</span>
                <button data-testid={`bodyweight-delete-${e.id}`} onClick={() => remove(e.id)}
                  className="text-[#555] hover:text-[#FF3B30] p-1 transition-colors"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};
